import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import Button from '@atlaskit/button/standard-button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import PremiumIcon from '@atlaskit/icon/glyph/premium';
import { token } from '@atlaskit/tokens';

import { getProfileUpgradeBannerPieces } from 'src/redux/profile/selectors';
import { getIsShowUpgradePlansBanner } from 'src/selectors/feature-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import urls from 'src/urls';
import { publishUiEvent } from 'src/utils/analytics/publish';
import store from 'src/utils/store';

import messages from './profile-upgrade-banner.i18n';
import * as styles from './profile-upgrade-banner.style';

const ProfileUpgradeBanner = () => {
  const upgradeBannerPieces = useSelector(
    getProfileUpgradeBannerPieces,
    shallowEqual
  );
  const currentUser = useSelector(getCurrentUser, shallowEqual);
  const isShowUpgradePlansBanner = useSelector(
    getIsShowUpgradePlansBanner,
    shallowEqual
  );
  const UPGRADE_BANNER_DISMISSED_LOCALSTORAGE_KEY = `upgrade_banner_dismissed::${
    currentUser ? currentUser.uuid : 'anonymous'
  }`;
  const [isBannerVisible, setBannerVisible] = useState(
    !store.get(UPGRADE_BANNER_DISMISSED_LOCALSTORAGE_KEY)
  );

  if (
    !currentUser ||
    !isShowUpgradePlansBanner ||
    !isBannerVisible ||
    !upgradeBannerPieces ||
    !upgradeBannerPieces.isAdmin ||
    upgradeBannerPieces.hasPremium
  ) {
    return null;
  }

  return (
    <styles.BannerContainer>
      <PremiumIcon
        size="medium"
        primaryColor={token('color.icon.information')}
        label=""
      />
      <Button
        appearance="subtle-link"
        iconBefore={<CrossIcon label="close" />}
        onClick={() => {
          setBannerVisible(false);
          store.setStrictlyNecessaryItem(
            UPGRADE_BANNER_DISMISSED_LOCALSTORAGE_KEY,
            true
          );
        }}
        className="close-button"
      />
      <styles.Title>
        <FormattedMessage {...messages.header} />
      </styles.Title>
      <styles.Description>
        <FormattedMessage {...messages.description} />
      </styles.Description>
      <CustomThemeButton
        appearance="primary"
        href={urls.ui.adminPlans}
        onClick={() =>
          publishUiEvent({
            action: 'clicked',
            actionSubject: 'upgradeButton',
            actionSubjectId: 'upgradeButtonRepositoriesList',
            source: 'emptyRepositoriesList',
            containerId: upgradeBannerPieces.uuid,
            containerType: 'workspace',
          })
        }
      >
        <FormattedMessage {...messages.upgrade} />
      </CustomThemeButton>
    </styles.BannerContainer>
  );
};

export default React.memo(ProfileUpgradeBanner);
